import type { SignKeyInfo } from '@/types/SignKeyInfo';
import type { UserInfo } from '@/types/deepadmin/UserInfo';
import { SignatureMode } from '@/types/enums/SignatureMode';
import { assign, fromPromise, setup } from 'xstate';

type MinimalUser = Pick<UserInfo, 'email'>;

interface SignStoreLike {
  fetchSignKeyInfo: (signKey: string) => Promise<unknown>;
}

interface Context {
  isInvite: boolean;
  user: MinimalUser | undefined;
  invitationLink: string | undefined;
  info: SignKeyInfo | undefined;
  signKey: string | string[] | undefined;
  signStore: SignStoreLike | undefined;
}

type Events =
  | { type: 'LOGIN' }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_INFO'; data: SignKeyInfo };

export const inviteMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>
  },
  actors: {
    getKeyInfo: fromPromise(async ({ input }: { input: Context }) => {
      return getSignKeyInfo(input.signKey, input.signStore);
    })
  },
  guards: {
    isInvitePage: ({ context }) => context.isInvite,

    isInviteUserMatching: ({ context }) =>
      isInvitationForLoggedInUser(context.user, context.info),

    isValidLink: ({ context }) =>
      isInvitationLinkValid(
        context.invitationLink,
        context.info,
        context.isInvite
      ),

    isPartiallyValid: ({ context }) =>
      isInvitationLinkPartiallyValid(context.invitationLink),

    isUserLoggedIn: ({ context }) => !!context.user?.email,
    isSimpleCase: ({ context }) => isSimpleCase(context.info),
    isAdvancedCase: ({ context }) => isAdvancedCase(context.info)
  }
}).createMachine({
  id: 'invite',
  type: 'parallel',
  context: ({ input }) => ({
    isInvite: input.isInvite ?? false,
    user: input.user,
    invitationLink: input.invitationLink,
    info: input.info,
    signKey: input.signKey,
    signStore: input.signStore
  }),
  states: {
    user: {
      initial: 'loggedOut',
      states: {
        loggedOut: {
          on: {
            LOGIN: { target: 'loggedIn' }
          }
        },
        loggedIn: {
          on: {
            LOGOUT: { target: 'loggedOut' }
          }
        }
      }
    },

    document: {
      initial: 'idle',
      states: {
        idle: {
          initial: 'checkingRoute',
          states: {
            checkingRoute: {
              always: [
                { target: 'loading', guard: 'isInvitePage' },
                { target: '#invite.document.ready.access.allowed' }
              ]
            },
            loading: {
              invoke: {
                id: 'loader',
                src: 'getKeyInfo',
                input: ({ context }) => context,
                onDone: {
                  target: '#invite.document.ready',
                  actions: assign({
                    info: ({ event }) => {
                      //eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const out = event.output as any;
                      return (
                        (out?.data as SignKeyInfo | undefined) ?? undefined
                      );
                    }
                  })
                },
                onError: {
                  target: '#invite.document.ready.access.disallowed'
                }
              }
            }
          }
        },

        ready: {
          type: 'parallel',
          states: {
            access: {
              initial: 'verifyingUserState',
              states: {
                verifyingUserState: {
                  always: [
                    { target: 'verifyingUser', guard: 'isUserLoggedIn' },
                    { target: 'parsingInviteLink' }
                  ]
                },
                verifyingUser: {
                  always: [
                    { target: 'allowed', guard: 'isInviteUserMatching' },
                    { target: 'disallowed' }
                  ]
                },
                parsingInviteLink: {
                  always: [
                    { target: 'allowed', guard: 'isValidLink' },
                    { target: 'allowed', guard: 'isPartiallyValid' },
                    { target: 'disallowed' }
                  ]
                },
                allowed: {},
                disallowed: {}
              }
            },

            signatureMode: {
              initial: 'idle',
              states: {
                idle: {
                  always: [
                    { target: 'simple', guard: 'isSimpleCase' },
                    { target: 'advanced', guard: 'isAdvancedCase' },
                    { target: 'qualified' }
                  ]
                },
                simple: {},
                advanced: {},
                qualified: {}
              }
            }
          }
        }
      }
    }
  }
});

function isInvitationLinkValid(
  link?: string,
  signKeyInfo?: SignKeyInfo,
  isInvite?: boolean
) {
  if (!link) return false;
  if (!isInvite) return true;

  let linkUrl: URL;
  try {
    linkUrl = new URL(link);
  } catch {
    return false;
  }

  const params = linkUrl.searchParams;

  if (signKeyInfo?.signatureMode !== SignatureMode.TIMESTAMP) {
    return !!params.get('mail-ref');
  }

  return !!(params.get('sid') && params.get('mail-ref'));
}

function isInvitationLinkPartiallyValid(link?: string) {
  if (!link) return false;

  let linkUrl: URL;
  try {
    linkUrl = new URL(link);
  } catch {
    return false;
  }

  return !!linkUrl.searchParams.get('sid');
}

function isInvitationForLoggedInUser(
  user?: MinimalUser,
  signKeyInfo?: SignKeyInfo
) {
  if (!user?.email || !signKeyInfo?.email) return false;
  return user.email.toLowerCase() === signKeyInfo.email.toLowerCase();
}

function getSignKeyInfo(
  signKey: string | string[] | undefined,
  signStore: SignStoreLike | undefined
) {
  const key = Array.isArray(signKey) ? signKey[0] : signKey;
  if (!key) return Promise.resolve(undefined);
  if (!signStore?.fetchSignKeyInfo) return Promise.resolve(undefined);
  return signStore.fetchSignKeyInfo(key);
}

function isSimpleCase(signKeyInfo?: SignKeyInfo) {
  return signKeyInfo?.signatureMode === SignatureMode.TIMESTAMP;
}
function isAdvancedCase(signKeyInfo?: SignKeyInfo) {
  return signKeyInfo?.signatureMode === SignatureMode.ADVANCED;
}
